import { useState, useRef, useEffect, useCallback } from 'react';

interface UseAutoSaveProps {
  onSaveDraft?: (content: string, draftId?: string) => string | Promise<string>;
  isEditingDraft: boolean;
}

/**
 * Manages auto-save debounce logic for the chat input.
 * Returns state and triggers for the parent component.
 */
export function useAutoSave({ onSaveDraft, isEditingDraft }: UseAutoSaveProps) {
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  const triggerAutoSave = useCallback(
    (content: string) => {
      if (!isEditingDraft && onSaveDraft && content.trim()) {
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
        }

        setIsAutoSaving(true);

        autoSaveTimerRef.current = setTimeout(async () => {
          try {
            const draftId = await onSaveDraft(
              content.trim(),
              activeDraftId || undefined
            );
            setActiveDraftId(draftId);
          } catch (error) {
            console.error('Failed to save draft:', error);
          } finally {
            setIsAutoSaving(false);
          }
        }, 1000);
      } else if (!content.trim()) {
        setIsAutoSaving(false);
        if (activeDraftId) {
          setActiveDraftId(null);
        }
      }
    },
    [isEditingDraft, onSaveDraft, activeDraftId]
  );

  const clearAutoSave = useCallback(() => {
    setActiveDraftId(null);
  }, []);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  return { isAutoSaving, triggerAutoSave, clearAutoSave };
}
